import parse from 'html-react-parser';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  IeltsListeningUserAttemptQuery,
  IeltsReadingUserAttemptQuery,
  IeltsSkill,
  IeltsWritingUserAttemptQuery,
} from '@/__generated__/graphql';
import IeltsSelect from '@/components/ielts/common/Inputs/Select';
import { activeListeningPartAtom, activeReadingPartAtom } from '@/store/ieltsListeningReading';
import {
  IeltsListeningReadingWritingFormValues,
  IeltsListeningReadingWritingQuestion,
} from '@/types';
import useIeltsListening from '../../../../listening/CheckPoint/DoExam/useIeltsListening';
import { configQuestionContent } from '../config';
import useIeltsReading from '@/components/ielts/reading/CheckPoint/DoExam/useIeltsReading';
import { ieltsWritingCurrentPartAtom } from '@/store/ieltsWriting';
import useDoIeltsWriting from '@/components/ielts/writing/CheckPoint/useDoIeltsWriting';
import useIeltsTheme from '../../../hooks/useIeltsTheme';

type Props = {
  question: IeltsListeningReadingWritingQuestion;
  isViewReport?: boolean;
  questionAttempt?: NonNullable<
    | NonNullable<IeltsListeningUserAttemptQuery['userAttempt']>['ieltsListeningPartAttempts']
    | NonNullable<IeltsReadingUserAttemptQuery['userAttempt']>['ieltsReadingPartAttempts']
    | NonNullable<IeltsWritingUserAttemptQuery['userAttempt']>['ieltsWritingPartAttempts']
  >[number]['questionAttempts'][number];
  type: IeltsSkill.Listening | IeltsSkill.Reading | IeltsSkill.Writing;
};

export default function IeltsListeningReadingOptionQuestion({
  question,
  questionAttempt,
  isViewReport,
  type,
}: Props) {
  const { watch } = useFormContext<IeltsListeningReadingWritingFormValues>() || {};
  const { theme } = useIeltsTheme();

  const details = useMemo(() => question.node.details, [question.node.details]);
  const activeListeningPart = useAtomValue(activeListeningPartAtom);
  const activeReadingPart = useAtomValue(activeReadingPartAtom);
  const activeWritingPart = useAtomValue(ieltsWritingCurrentPartAtom);
  const activePart =
    type === IeltsSkill.Listening
      ? activeListeningPart
      : type === IeltsSkill.Writing
        ? activeWritingPart
        : activeReadingPart;
  const partQuestions =
    (type === IeltsSkill.Listening
      ? activeListeningPart?.node.ieltsListeningPartQuestions
      : type === IeltsSkill.Writing
        ? activeWritingPart?.node.ieltsWritingPartQuestions
        : activeReadingPart?.node.ieltsReadingPartQuestions) || [];
  const questionIndex = partQuestions.findIndex((q) => q.node.id === question.node.id);

  const partIndex = isViewReport
    ? 0
    : watch('parts').findIndex((p) => p.partId === activePart?.node.id);

  const { handleSubmit: listeningHandleSubmit } = useIeltsListening();
  const { handleSubmit: readingHandleSubmit } = useIeltsReading();
  const { handleSubmitPracticeIeltsPart } = useDoIeltsWriting();
  const handleSubmit =
    type === IeltsSkill.Listening
      ? listeningHandleSubmit
      : type === IeltsSkill.Writing
        ? handleSubmitPracticeIeltsPart
        : readingHandleSubmit;

  if (partIndex === -1) return null;

  return (
    <div>
      <p
        className="text-lg max-md:text-base font-bold mb-[5px]"
        style={{
          color: theme.colorPrimary,
        }}
      >
        {question.node.title}
      </p>
      <div className="mb-4 max-md:mb-2">
        {parse(question.node.contentHtml, {
          replace: (domNode) => {
            if (domNode.type === 'tag' && domNode.name === 'blank') {
              const detail = details.find((d) => d.id === domNode.attribs.id);
              if (!detail) return domNode;

              const detailAttempt = questionAttempt?.detailAnswers.find(
                (d) => d.detailId === detail.id,
              );

              const detailAnswers = isViewReport
                ? []
                : watch(`parts.${partIndex}.questions.${questionIndex}.detailAnswers`);

              const detailAnswer = isViewReport
                ? detailAttempt
                : detailAnswers?.find((d) => d.detailId === detail.id);

              const correctOption = detail.options?.find((o) => o.correctAnswer);

              const result = (() => {
                if (!isViewReport || !correctOption) return;
                return correctOption.id === detailAnswer?.optionId;
              })();

              return (
                <span key={detail.id} className="inline-flex items-center flex-wrap gap-1">
                  <IeltsSelect
                    className="inline-flex my-1"
                    id={detail.id}
                    value={detailAnswer?.optionId || ''}
                    readonly={isViewReport}
                    result={result}
                    showIcon={!isViewReport}
                    options={
                      detail.options?.map((o) => {
                        return {
                          id: o.id,
                          label: o.label,
                          value: o.id,
                          title: o.text,
                        };
                      }) || []
                    }
                    onSelect={(optionId) =>
                      handleSubmit({
                        questionId: question.node.id,
                        detailAnswer: {
                          detailId: detail.id,
                          optionId,
                        },
                      })
                    }
                  />
                </span>
              );
            }
            return configQuestionContent(domNode, theme);
          },
        })}
      </div>
    </div>
  );
}
